import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { InventoryItemDTO } from 'src/app/models/results/inventory-item-dto';
import { DataFilteringService } from 'src/app/models/interfaces/data-filtering-service';
import { InventoryQueryParamsService } from 'src/app/models/interfaces/inventory-query-params-service';
import { InventoryCheckboxFilterModel } from 'src/app/models/results/inventory-checkbox-filter';
import { InventoryGroup } from 'src/app/models/results/inventory-group';
import { InventoryItemGroup } from 'src/app/models/results/inventory-item-group';
import { InventoryItemViewModel } from 'src/app/models/results/inventory-item-view-model';
import { InventoryQueryParams } from 'src/app/models/results/inventory-query-params';
import { InventorySearchResult } from 'src/app/models/results/inventory-search-result';
import { ResultConfigViewModel } from 'src/app/models/results/result-config-view-model';
import { DataService } from '../data/data.service';
import { WizardStepName } from '../wizard/wizard-step-name.enum';
import { WizardService } from '../wizard/wizard.service';
import { EducationFilteringService } from './education/education-filtering.service';
import { EducationInventoryQueryParamsService } from './education/education-inventory-query-params.service';
import { EnterpriseFilteringService } from './enterprise/enterprise-filtering.service';
import { EnterpriseInventoryQueryParamsService } from './enterprise/enterprise-inventory-query-params.service';
import { ConfigService } from '../config/config.service';
import { SharedFilteringService } from './shared-filtering.service';
import { PriceSort } from 'src/app/models/results/price-sort';

@Injectable({
  providedIn: 'root'
})
export class ResultsService {
  queryParams: InventoryQueryParams = new InventoryQueryParams();
  originalItems: InventoryItemDTO[] = [];

  constructor(
    private confSvc: ConfigService,
    private dataService: DataService,
    private wizardService: WizardService,
    private enterpriseFilteringService: EnterpriseFilteringService,
    private enterpriseQueryParamsService: EnterpriseInventoryQueryParamsService,
    private educationFilteringService: EducationFilteringService,
    private educationQueryParamsService: EducationInventoryQueryParamsService,
    private sharedFilteringService: SharedFilteringService) { }

  getEnterpriseInventoryItems(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel, priceSort?: PriceSort): Observable<InventorySearchResult> {
    return this.getInventoryItems(this.enterpriseQueryParamsService, this.enterpriseFilteringService, filterItemsFromCheckboxParams, priceSort);
  }

  getEducationInventoryItems(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel, priceSort?: PriceSort): Observable<InventorySearchResult> {
    return this.getInventoryItems(this.educationQueryParamsService, this.educationFilteringService, filterItemsFromCheckboxParams, priceSort);
  }

  getInventoryItems(
    queryParamsService: InventoryQueryParamsService,
    filteringService: DataFilteringService,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel,
    priceSort?: PriceSort): Observable<InventorySearchResult> {

    const selectedNeedToRunOptions$ = this.wizardService.getSelectedWizardOptionsForStep(WizardStepName.NeedToRun);
    const selectedUseCaseOptions$ = this.wizardService.getSelectedWizardOptionsForStep(WizardStepName.UseCase);
    const selectedBrandOptions$ = this.wizardService.getSelectedWizardOptionsForStep(WizardStepName.Brands);

    return forkJoin([selectedNeedToRunOptions$, selectedUseCaseOptions$, selectedBrandOptions$]).pipe(
      switchMap(([needToRunOptions, useCaseOptions, brandOptions]) => {
        this.queryParams = new InventoryQueryParams();
        queryParamsService.setFields(this.queryParams, needToRunOptions, useCaseOptions[0], brandOptions);

        return this.dataService.executeDatabaseQuery();
      }),
      map((items: InventoryItemDTO[]) => {
        // use !== false rather than === true because this will also include documents that don't already have the ShowInResults field
        this.originalItems = items.filter(item => item.showInResults !== false);

        const filteredItems = this.getFilteredAndSortedItems(filteringService, this.queryParams, filterItemsFromCheckboxParams, priceSort);
        const viewModels = this.getMappedViewModels(filteredItems);
        const itemGroups: InventoryItemGroup[] = this.groupItems(viewModels);
        const enableLoadMore = this.loadMoreShouldBeEnabled(itemGroups);

        return new InventorySearchResult(itemGroups, enableLoadMore);
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  getFilteredAndSortedItems(
    filteringService: DataFilteringService,
    queryParams: InventoryQueryParams,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel,
    priceSort?: PriceSort): InventoryItemDTO[] {

    let filteredResults: InventoryItemDTO[] = [];

    this.filterItems(filteringService, filteredResults, queryParams, queryParams.groups[0], filterItemsFromCheckboxParams);
    filteredResults = filteringService.sortItems(filteredResults, this.queryParams, priceSort);

    return filteredResults;
  }

  filterItems(
    filteringService: DataFilteringService,
    filteredResults: InventoryItemDTO[],
    queryParams: InventoryQueryParams,
    group: InventoryGroup,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): void {
      for (let round = 1; round <= filteringService.getRoundCount(); round++) {
        if (this.shouldRunRoundFiltering(round, queryParams)) {
          this.runRoundFiltering(filteringService, filteredResults, queryParams, round, group, filterItemsFromCheckboxParams);
        }
      }
  }

  shouldRunRoundFiltering(round: number, queryParams: InventoryQueryParams): boolean {
    return (round === 1) || (queryParams.nonParallelsOptionSelected);
  }

  runRoundFiltering(
    filteringService: DataFilteringService,
    filteredResults: InventoryItemDTO[],
    queryParams: InventoryQueryParams,
    round: number,
    group: InventoryGroup,
    filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): void {
      let itemsToFilter: InventoryItemDTO[] = JSON.parse(JSON.stringify(this.originalItems));

      itemsToFilter = filteringService.applyFilteringByRound(itemsToFilter, queryParams, group, round, filterItemsFromCheckboxParams);

      this.addNonDuplicateItemsToResult(itemsToFilter, filteredResults);
  }

  addNonDuplicateItemsToResult(itemsToFilter: InventoryItemDTO[], filteredResults: InventoryItemDTO[]): void {
    itemsToFilter.forEach(item => {
      if (!filteredResults.some(result => result.manufacturePart === item.manufacturePart)) {
        filteredResults.push(item)
      }
    });
  }

  getMappedViewModels(items: InventoryItemDTO[]): InventoryItemViewModel[] {
    return items.map(item => {
      return this.mapViewModel(item);
    });
  }

  mapViewModel(item: InventoryItemDTO): InventoryItemViewModel {
    return Object.assign(new InventoryItemViewModel(), {
      cpuModel: item.cpuModel,
      diskDriveSize: item.diskDriveSize,
      diskDriveUnit: item.diskDriveUnit,
      diskDriveType: item.diskDriveType,
      imageLocation: item.imageLargeLocation,
      manufactureName: item.manufactureName,
      manufacturePartNum: item.manufacturePart,
      materialID: item.materialID,
      memorySize: item.memorySize,
      memoryUnit: item.memoryUnit,
      name: item.name,
      osType: this.sharedFilteringService.isEnterpriseDevice(item) ? this.confSvc.getString("enterpriseFilterLabel") : this.confSvc.getString("nonEnterpriseFilterLabel"),
      productDescLong: item.productDescLong,
      productDescShort: item.productDescShort,
      productLine: item.productLine,
      productType: item.productType,
      screenSize: item.formattedScreenSize,
      vendorURL: item.vendorURL,
      id: item.id
    });
  }

  groupItems(viewModels: InventoryItemViewModel[]): InventoryItemGroup[] {
    const itemGroups: InventoryItemGroup[] = [];

    viewModels.forEach(vm => {
      const matchingItemGroup = this.getMatchingGroup(itemGroups, vm);

      if (matchingItemGroup) {
        matchingItemGroup.secondaryItems.push(vm);
      } else {
        itemGroups.push(new InventoryItemGroup(vm, []));
      }
    });

    return itemGroups;
  }

  getMatchingGroup(itemGroups: InventoryItemGroup[], vm: InventoryItemViewModel): InventoryItemGroup | undefined {
    return itemGroups.find(group => {
      const nameMatches = group.primaryItem.name === vm.name;
      const productLineMatches = this.productLineIsValidAndMatches(group, vm);
      const materialIdPrefixMatches = this.materialIdPrefixMatches(group, vm);

      return nameMatches || productLineMatches || materialIdPrefixMatches;
    });
  }

  productLineIsValidAndMatches(group: InventoryItemGroup, vm: InventoryItemViewModel): boolean {
    const productLineValid = !!vm.productLine && vm.productLine.split(' ').length > 2; // disregard generic 2-word product lines like "Dell Chromebook", "HP Elite", "Acer Chromebook" etc.
    const productLineMatches = (group.primaryItem.productLine === vm.productLine);

    return productLineValid && productLineMatches;
  }

  materialIdPrefixMatches(group: InventoryItemGroup, vm: InventoryItemViewModel): boolean {
    // this is to cover scenarios where an item doesn't have ProductLine populated but has a unique enough MaterialID
    // to match on, like CHROMEBOX3-N7068U since each Chromebox 3 has that MaterialID prefix
    const groupItemMaterialIdPrefix = group.primaryItem.materialID.split('-')[0];
    const vmMaterialIdPrefix = vm.materialID.split('-')[0];

    return groupItemMaterialIdPrefix === vmMaterialIdPrefix;
  }

  loadMoreShouldBeEnabled(itemGroups: InventoryItemGroup[]): boolean {
    return itemGroups.length > this.confSvc.getNum("deviceResultsToDisplay");
  }

  getItemsForResultConfig(): Observable<ResultConfigViewModel[]> {
    return this.dataService.executeDatabaseQuery().pipe(
      map((dbItems: InventoryItemDTO[]) => {
        return this.getMappedResultConfigViewModels(dbItems)
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  getMappedResultConfigViewModels(items: InventoryItemDTO[]): ResultConfigViewModel[] {
    return items.map(item => {
      return this.mapResultConfigViewModel(item);
    });
  }

  mapResultConfigViewModel(item: InventoryItemDTO): ResultConfigViewModel {
    return Object.assign(new ResultConfigViewModel(), {
      id: item.id,
      materialID: item.materialID,
      productDescShort: item.productDescShort,
      showInResults: item.showInResults
    });
  }
}
